import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { MessageSquare, Phone, Mail, Loader2, Send, Copy, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import SupportAnalytics from "./SupportAnalytics";

const ICON = { chat: MessageSquare, phone: Phone, email: Mail };
const STATUS_CLS = { open: "bg-amber-500/20 text-amber-300 border-amber-500/30", replied: "bg-blue-500/20 text-blue-300 border-blue-500/30", closed: "bg-slate-700/40 text-slate-300 border-slate-600" };

function SetupCard({ cfg }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  const Row = ({ label, value, tid }) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-500 w-28 shrink-0">{label}</span>
      <code className="font-mono text-slate-300 truncate flex-1" data-testid={tid}>{value || "—"}</code>
      {value && <button onClick={() => copy(value)} className="text-slate-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>}
    </div>
  );
  return (
    <div className="p-4 rounded-xl bg-[#131722] border border-slate-800 space-y-2" data-testid="support-setup-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Channels · {cfg.model}</p>
        <div className="flex gap-1.5">
          <Badge className={`border text-[10px] ${cfg.twilio_configured ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border-slate-600"}`} data-testid="twilio-status-badge">Phone {cfg.twilio_configured ? "LIVE" : "MOCK"}</Badge>
          <Badge className="border text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Email AUTO-REPLY</Badge>
        </div>
      </div>
      <Row label="Voice webhook" value={cfg.voice_webhook} tid="cfg-voice-webhook" />
      <Row label="Status callback" value={cfg.voice_status_webhook} tid="cfg-voice-status" />
      <Row label="Email inbound" value={cfg.email_webhook} tid="cfg-email-webhook" />
      <p className="text-[11px] text-slate-500">Point your Twilio number's Voice webhook and your mail provider's inbound-parse (SendGrid/Mailgun/Resend) to these URLs. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER secrets to go live on phone.</p>
    </div>
  );
}

function CallSimulator({ onTicket }) {
  const [sid, setSid] = useState(null);
  const [text, setText] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const turn = async (hangup = false) => {
    if (!text.trim() && !hangup) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/support/voice/simulate", { call_sid: sid, speech: text.trim() || "goodbye", hangup });
      setSid(data.call_sid);
      setLog((l) => [...l, { role: "caller", text: text.trim() || "goodbye" }, { role: "agent", text: data.reply }]);
      setText("");
      if (data.ticket_id) { toast.success(`Call logged as ticket ${data.ticket_id.slice(-6)}`); setSid(null); onTicket?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl bg-[#131722] border border-slate-800 space-y-3" data-testid="call-simulator">
      <p className="text-xs font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" />Test the phone agent</p>
      <div className="max-h-40 overflow-y-auto space-y-1.5 text-xs" data-testid="call-sim-log">
        {log.length === 0 && <p className="text-slate-500">Type what a caller would say to hear how the AI agent responds.</p>}
        {log.map((m, i) => <p key={`${i}-${m.role}`} className={m.role === "caller" ? "text-slate-300" : "text-cyan-300"}><span className="font-mono text-slate-500">{m.role}: </span>{m.text}</p>)}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && turn()} placeholder="Caller says…" data-testid="call-sim-input" className="bg-slate-900 border-slate-800 h-9" />
        <Button size="sm" onClick={() => turn()} disabled={busy || !text.trim()} data-testid="call-sim-send" className="bg-white text-slate-950 hover:bg-slate-100">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Say"}</Button>
        <Button size="sm" variant="outline" onClick={() => turn(true)} disabled={busy || !sid} data-testid="call-sim-hangup" className="bg-transparent border-slate-700 text-red-300">Hang up</Button>
      </div>
    </div>
  );
}

function TicketDetail({ id, onChanged }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => { const { data } = await api.get(`/admin/support/tickets/${id}`); setT(data); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!t) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const act = async (key, fn, ok) => { setBusy(key); try { await fn(); toast.success(ok); await load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); } };
  const c = t.contact || {};
  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <div className="p-4 rounded-xl bg-[#131722] border border-slate-800 space-y-1.5 text-sm">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold" data-testid="ticket-subject">{t.subject}</h3>
          <Badge className={`border capitalize ${STATUS_CLS[t.status]}`} data-testid="ticket-status-badge">{t.status}</Badge>
        </div>
        <p className="text-slate-400">{t.summary}</p>
        <p className="text-xs text-slate-500 font-mono">{t.channel} · {t.sentiment} · {c.name || ""} {c.email || ""} {c.phone || ""} {c.callback_number ? `· callback ${c.callback_number}` : ""} {t.mock ? "· simulated" : ""}</p>
        <div className="flex gap-2 pt-1">
          {["open", "replied", "closed"].filter((s) => s !== t.status).map((s) => (
            <Button key={s} size="sm" variant="outline" disabled={busy === s} data-testid={`ticket-set-${s}`}
              onClick={() => act(s, () => api.post(`/admin/support/tickets/${id}/status`, { status: s }), `Marked ${s}`)}
              className="bg-transparent border-slate-700 capitalize h-7 text-xs">Mark {s}</Button>
          ))}
        </div>
      </div>
      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-2 max-h-[40vh] overflow-y-auto" data-testid="ticket-transcript">
        {(t.transcript || []).map((m, i) => (
          <div key={m.at || m.ts || `${i}-${m.role}`} className={`text-sm ${m.role === "user" ? "text-slate-200" : m.role === "agent" ? "text-emerald-300" : "text-cyan-300"}`}>
            <span className="font-mono text-[11px] text-slate-500 uppercase">{m.role === "user" ? (t.channel === "phone" ? "caller" : "customer") : m.role === "agent" ? `you (${m.by})` : "ai"}: </span>
            <span className="whitespace-pre-wrap">{m.text}</span>
          </div>
        ))}
      </div>
      {c.email && (
        <div className="space-y-2">
          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to ${c.email}…`} data-testid="ticket-reply-input" className="bg-slate-900 border-slate-800 min-h-[80px]" />
          <Button size="sm" disabled={busy === "reply" || !reply.trim()} data-testid="ticket-reply-send"
            onClick={() => act("reply", () => api.post(`/admin/support/tickets/${id}/reply`, { message: reply }).then(() => setReply("")), "Reply sent")}
            className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold">{busy === "reply" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send email reply</>}</Button>
        </div>
      )}
    </div>
  );
}

export default function SupportInbox() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("open");
  const [channel, setChannel] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/support/tickets?status=${status === "all" ? "" : status}&channel=${channel}`);
    setItems(data.items); setCounts(data.counts);
  }, [status, channel]);
  useEffect(() => { api.get("/admin/support/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 rise rise-2" data-testid="support-inbox">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SupportAnalytics />
        {cfg && <SetupCard cfg={cfg} />}
        <CallSimulator onTicket={load} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        <section>
          <Tabs value={status} onValueChange={(v) => { setStatus(v); setSelected(null); }}>
            <TabsList className="bg-slate-900/60 border border-slate-800 w-full">
              {["open", "replied", "closed", "all"].map((s) => <TabsTrigger key={s} value={s} data-testid={`ticket-filter-${s}`} className="flex-1 capitalize">{s}{counts[s] != null ? ` (${counts[s]})` : ""}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="flex gap-1.5 mt-2">
            {["", "chat", "phone", "email"].map((ch) => (
              <button key={ch} onClick={() => setChannel(ch)} data-testid={`ticket-channel-${ch || "all"}`}
                className={`px-2.5 py-1 rounded-full text-xs border capitalize transition-colors ${channel === ch ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>{ch || "all channels"}</button>
            ))}
          </div>
          <div className="mt-3 space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="ticket-list">
            {items.length === 0 && <p className="text-sm text-slate-500 p-6 text-center border border-dashed border-slate-800 rounded-xl">No tickets here.</p>}
            {items.map((t) => { const I = ICON[t.channel] || MessageSquare; return (
              <button key={t.ticket_id} onClick={() => setSelected(t.ticket_id)} data-testid={`ticket-${t.ticket_id}`}
                className={`w-full text-left p-3.5 rounded-xl border transition-colors ${selected === t.ticket_id ? "bg-[#161b28] border-cyan-500/50" : "bg-[#131722] border-slate-800 hover:border-slate-600"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold truncate flex items-center gap-2"><I className="w-3.5 h-3.5 text-cyan-400 shrink-0" />{t.subject}</p>
                  <Badge className={`border capitalize text-[10px] ${STATUS_CLS[t.status]}`}>{t.status}</Badge>
                </div>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.summary}</p>
                <p className="text-[11px] text-slate-500 font-mono mt-1">{new Date(t.created_at).toLocaleString()} · {t.contact?.email || t.contact?.phone || "visitor"}</p>
              </button>
            ); })}
          </div>
        </section>
        <section className="min-h-[300px]">
          {selected ? <TicketDetail id={selected} onChanged={load} /> :
            <div className="h-full min-h-[300px] flex items-center justify-center rounded-2xl border border-dashed border-slate-800 text-slate-500 text-sm" data-testid="ticket-empty">Select a ticket</div>}
        </section>
      </div>
    </div>
  );
}
